import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Text, useToast } from "@chakra-ui/react";
import { api, endSession, setSession, tokenExpiresAt } from "../lib/api";

// Logins last 7 days. Before this banner, the first sign a login had ended was
// a sudden bounce to the sign-in page (Oct 2026). Now people get a day's
// notice and a one-click renewal, and an expired login says so on the way out.
const WARN_MS = 24 * 60 * 60 * 1000;
const TICK_MS = 60 * 1000;

function describe(expiresAt, now) {
  const end = new Date(expiresAt);
  const time = end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const today = new Date(now);
  const tomorrow = new Date(now + 24 * 60 * 60 * 1000);
  if (end.toDateString() === today.toDateString()) return `today at ${time}`;
  if (end.toDateString() === tomorrow.toDateString()) return `tomorrow at ${time}`;
  return `${end.toLocaleDateString([], { weekday: "long" })} at ${time}`;
}

export default function SessionExpiryBanner() {
  const toast = useToast();
  const [now, setNow] = useState(Date.now());
  const [expiresAt, setExpiresAt] = useState(() => tokenExpiresAt());
  const [hiddenFor, setHiddenFor] = useState(null);
  const [busy, setBusy] = useState(false);

  // Re-read the token each tick too, so a renewal in another tab shows up here.
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
      setExpiresAt(tokenExpiresAt());
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (expiresAt && expiresAt <= now) endSession("session_expired");
  }, [expiresAt, now]);

  if (!expiresAt || expiresAt <= now) return null;
  if (expiresAt - now > WARN_MS) return null;
  if (hiddenFor === expiresAt) return null;

  async function renew() {
    setBusy(true);
    try {
      const r = await api("/me/session/renew", { method: "POST" });
      if (r.token) {
        setSession({ token: r.token });
        setExpiresAt(tokenExpiresAt(r.token));
        toast({ title: "You're signed in for another 7 days", status: "success", duration: 3000 });
      }
    } catch (e) {
      toast({ title: "Couldn't extend your login", description: e.message, status: "error" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      role="status"
      px={{ base: 4, md: 6 }}
      py={2.5}
      style={{ background: "var(--tone-warning-bg)", color: "var(--tone-warning-fg)" }}
    >
      {/* Buttons sit right after the message, not pinned to the far edge, so
          they stay on screen when a wide page scrolls sideways. */}
      <Flex align="center" gap={3} wrap="wrap">
        <Text fontSize="sm" fontWeight={500}>
          Your login ends {describe(expiresAt, now)}. After that you'll need to sign in again.
        </Text>
        <Button size="sm" onClick={renew} isLoading={busy}>
          Stay signed in
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setHiddenFor(expiresAt)} color="inherit">
          Not now
        </Button>
      </Flex>
    </Box>
  );
}
